import {
  ExternalToken,
  PresignedUrlResponse,
  RequirementFeAction,
  RequirementFeActionConditionAttribute,
} from './types';
import { Engine, RuleProperties } from 'json-rules-engine';
import { gatewayOperator, listOperator } from './ruleOperators';
import config from '../config';
import { clearMethodsDrafts } from './methodsDraft';
import i18n from '../i18n';
import { hasStandaloneQrFlag, isStandaloneQrLocation } from './qrEntry';
import {
  DEFAULT_LANGUAGE,
  lazyLocales,
  SUPPORTED_LANGUAGES,
} from '../i18n/languages';

export const API_URL = config.apiUrl || 'http://localhost:8080/api';

// #72: a parcel QR opened full screen (or a reload of one of its pages - the
// router keeps its navigation state under `usr`) starts without a shop; it
// never picks up the shop this browser remembers (or the configured one) -
// the shop comes only from the verified QR redemption.
const startsAsStandaloneQr = (): boolean =>
  isStandaloneQrLocation(window.location.pathname, window.location.search) ||
  hasStandaloneQrFlag((window.history.state as { usr?: unknown } | null)?.usr);

export let SHOP_ID = startsAsStandaloneQr()
  ? 0
  : Number(localStorage.getItem('shopId')) || Number(config.shopId);

// #95: set by the language coordinator below: drops the labels of the
// previous shop when the shop changes
let onShopChange: () => void = () => undefined;

export const setShop = (id: number) => {
  const changed = !Object.is(id, SHOP_ID);
  SHOP_ID = id;
  if (changed) {
    // #496 (review r2 #1): a draft of another shop's flow never survives
    clearMethodsDrafts();
    onShopChange();
  }
};

export let JWT_TOKEN = '';

export const setToken = (token: string) => {
  JWT_TOKEN = token;
};

export const REFRESH_TIME = Number(config.refreshTime) || 60;

export const BASE_NAME = config.baseName || '';

export const ENV_TYPE = config.envType || 'local';

export const formatPrice = (price: number, currency: string): string => {
  if (price === null || price === 0) return i18n.t('buttons.free');
  return new Intl.NumberFormat(i18n.t('buttons.locale') ?? 'en-US', {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'code',
  }).format(price);
};

// Helper function to build the attribute path by traversing up the parent chain
export const buildAttributePath = (
  attribute: RequirementFeActionConditionAttribute
): string => {
  const path = [];
  let current: RequirementFeActionConditionAttribute | null = attribute;

  // Traverse up the parent chain to collect all attribute_keys
  while (current) {
    path.unshift(current.attribute_key); // Add to beginning to get correct order
    current = current.parent;
  }

  return path.join('.');
};

// Transform each condition in the conditions array
export const isNumericString = (str: string): boolean =>
  !Number.isNaN(Number(str)) && str.trim() !== '';

// Operators that keep their RAW text operand: an IN / NOT_IN list is split by
// registerListOperators ("02" must not become 2, #128) and CONTAINS /
// NOT_CONTAINS is a substring / element test (#133). Every other operator gets
// a number when the text is numeric (MVEL coerces "2" == 2).
const TEXT_OPERAND_OPERATORS = ['in', 'notIn', 'contains', 'doesNotContain'];
const operandOf = (operatorName: string, value: string): string | number =>
  !TEXT_OPERAND_OPERATORS.includes(operatorName) && isNumericString(value)
    ? Number(value)
    : value;

// #133: the json-rules-engine operators the portal engines know (defaults plus
// registerBetweenOperator / registerListOperators / registerTextOperators). A
// condition outside this set makes its rule unbuildable: the portal does not
// require a photo on a guess and one such rule cannot break the whole run.
const KNOWN_OPERATORS = [
  'equal',
  'notEqual',
  'in',
  'notIn',
  'numberIn',
  'numberNotIn',
  'contains',
  'doesNotContain',
  'lessThan',
  'lessThanInclusive',
  'greaterThan',
  'greaterThanInclusive',
  'between',
  'gatewayEquals',
  'gatewayNotEquals',
  'gatewayIn',
  'gatewayNotIn',
  'gatewayContains',
  'gatewayNotContains',
];

// #133: one json-rules-engine rule per REQUIRE_DEFECT_PHOTO rule, its known
// conditions ANDed as the server ANDs a rule's conditions (return-api
// RuleConditionMvelBuilder.buildMvelExpression); separate rules stay OR. The
// portal knows two product facts: the reason name (`reason`, text) and the
// returned quantity (`quantity`, number); a quantity operand is compared as a
// number, a reason operand as text. The order.payment_method fact is retained
// only alongside a reason / quantity condition. Other conditions (including
// `order.line_item.quantity`, the ORDERED
// quantity) are ignored here (known limitation, see README); a rule
// with none of the known facts, or with an operator the engine does not know,
// builds nothing and the server stays the judge.
export const buildPhotoRules = (
  feActions: RequirementFeAction[]
): RuleProperties[] =>
  feActions
    .filter(action => action.rule_action.action_type === 'REQUIRE_DEFECT_PHOTO')
    .map(action =>
      action.conditions
        .map(condition => ({
          path: buildAttributePath(condition.attribute),
          condition,
        }))
        // the known facts come from the return product (`product.*`, legacy
        // `return.*`); an `order.*` path - `order.line_item.quantity` is the
        // ORDERED quantity, not the returned one - is an order-level condition
        .filter(
          ({ path }, _, conditions) =>
            (path === 'order.payment_method' &&
              conditions.some(
                ({ path }) =>
                  !path.startsWith('order') &&
                  (path.includes('reason') || path.includes('quantity'))
              )) ||
            (!path.startsWith('order') &&
              (path.includes('reason') || path.includes('quantity')))
        )
        .map(({ path, condition }) => {
          const name = toCamelCase(condition.operator.code, {
            transformVerbs: true,
          });
          const fact = path.includes('reason') ? 'reason' : 'quantity';
          if (path === 'order.payment_method') {
            return {
              fact: path,
              operator: gatewayOperator(name),
              value: condition.value,
            };
          }
          return {
            fact,
            operator: listOperator(name, condition.operator.value_type),
            value:
              fact === 'quantity'
                ? operandOf(name, condition.value)
                : condition.value,
          };
        })
    )
    .filter(
      conditions =>
        conditions.length > 0 &&
        conditions.every(({ operator }) => KNOWN_OPERATORS.includes(operator))
    )
    .map(conditions => ({
      conditions: { all: conditions },
      event: {
        type: 'showField',
        params: { field: 'photo', message: 'Should require photo' },
      },
    }));

// #149: whether a photo rule built above depends on the return reason (unknown
// in withdrawal mode, where such a rule is skipped)
export const photoRuleReferencesReason = (rule: RuleProperties): boolean =>
  'all' in rule.conditions &&
  (rule.conditions.all as { fact?: string }[]).some(c => c.fact === 'reason');

// #87: one json-rules-engine rule per DENY_RETURN action that has at least one
// line_item condition; the conditions of that rule are ANDed (as the rule
// editor states and the server evaluates them), separate rules stay OR.
// Known limitation: order/return-level conditions inside such a rule are
// ignored except order.payment_method.
export const buildLineItemDenyRules = (
  feActions: RequirementFeAction[]
): RuleProperties[] =>
  feActions
    .filter(action => action.rule_action.action_type === 'DENY_RETURN')
    .filter(action =>
      action.conditions.some(condition =>
        buildAttributePath(condition.attribute).includes('line_item')
      )
    )
    .map(action =>
      action.conditions
        .map(condition => ({
          path: buildAttributePath(condition.attribute),
          condition,
        }))
        .filter(
          ({ path }) =>
            path.includes('line_item') || path === 'order.payment_method'
        )
        .map(({ path, condition }) => {
          const name = toCamelCase(condition.operator.code, {
            transformVerbs: true,
          });
          if (path === 'order.payment_method') {
            return {
              fact: path,
              operator: gatewayOperator(name),
              value: condition.value,
            };
          }
          return {
            fact: path.split('.').pop() || '',
            operator: listOperator(name, condition.operator.value_type),
            value: operandOf(name, condition.value),
          };
        })
    )
    .filter(conditions => conditions.length > 0)
    .map(conditions => ({
      conditions: { all: conditions },
      event: {
        type: 'grey-out-item',
        params: { shouldGreyOut: true },
      },
    }));

// "between" accepts [min, max] or "min,max"; both bounds are inclusive.
export const registerBetweenOperator = (engine: Engine): void => {
  engine.addOperator(
    'between',
    (factValue: unknown, jsonValue: unknown): boolean => {
      let min: number, max: number;

      if (Array.isArray(jsonValue)) {
        if (jsonValue.length !== 2) {
          return false;
        }
        const [minVal, maxVal] = jsonValue;
        min = typeof minVal === 'number' ? minVal : parseFloat(String(minVal));
        max = typeof maxVal === 'number' ? maxVal : parseFloat(String(maxVal));

        if (isNaN(min) || isNaN(max)) {
          return false;
        }
      } else if (typeof jsonValue === 'string') {
        const parts = jsonValue.split(',').map(part => parseFloat(part.trim()));
        if (parts.length !== 2 || parts.some(isNaN)) {
          return false;
        }
        [min, max] = parts;
      } else {
        return false;
      }

      let numericFactValue: number;
      if (typeof factValue === 'number') {
        numericFactValue = factValue;
      } else if (typeof factValue === 'string') {
        numericFactValue = parseFloat(factValue);
      } else {
        return false;
      }

      if (isNaN(numericFactValue)) {
        return false;
      }

      return numericFactValue >= min && numericFactValue <= max;
    }
  );
};

export const toCamelCase = (
  str: string,
  options: {
    preserveNumbers?: boolean;
    removeAccents?: boolean;
    transformVerbs?: boolean;
  } = {}
): string => {
  const transformVerb = (word: string): string => {
    // Handle special cases and irregular verbs
    const irregularVerbs: Record<string, string> = {
      is: 'be',
      has: 'have',
      does: 'do',
      goes: 'go',
      says: 'say',
      gets: 'get',
      comes: 'come',
      gives: 'give',
      takes: 'take',
      makes: 'make',
      knows: 'know',
      sees: 'see',
      finds: 'find',
      tells: 'tell',
      asks: 'ask',
      uses: 'use',
      works: 'work',
      seems: 'seem',
      feels: 'feel',
      looks: 'look',
      turns: 'turn',
      shows: 'show',
      helps: 'help',
      moves: 'move',
      lives: 'live',
      brings: 'bring',
      sits: 'sit',
      runs: 'run',
      puts: 'put',
      equals: 'equal',
      contains: 'contains',
    };

    // Check irregular verbs first (case-insensitive)
    const lowerWord = word.toLowerCase();
    if (irregularVerbs[lowerWord]) {
      return irregularVerbs[lowerWord];
    }

    // Handle words ending in 'ies' -> 'y'
    if (word.endsWith('ies') && word.length > 3) {
      return word.slice(0, -3) + 'y';
    }

    // Handle words ending in 'es' after s, x, z, ch, sh
    if (word.endsWith('es') && word.length > 2) {
      const beforeEs = word.slice(-3, -2);
      if (
        ['s', 'x', 'z'].includes(beforeEs) ||
        word.endsWith('ches') ||
        word.endsWith('shes')
      ) {
        return word.slice(0, -2);
      }
    }

    // Handle words ending in 'ses' -> 'se'
    if (word.endsWith('ses') && word.length > 3) {
      return word.slice(0, -1);
    }

    // Default case: remove 's' if the word ends with 's'
    if (word.endsWith('s') && word.length > 1) {
      return word.slice(0, -1);
    }

    return word;
  };

  if (!str) return '';

  let result = str.trim();

  // Remove accents if requested
  if (options.removeAccents) {
    result = result.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  // Split into words - handle both camelCase and delimiter-separated strings
  const camelCaseSplit = result.replace(/([a-z])([A-Z])/g, '$1 $2');
  const words = camelCaseSplit.split(/[^a-zA-Z0-9]+/);

  const transformedWords = words.map((word, index) => {
    if (!word) return word;

    let processedWord = word.toLowerCase();

    // Transform verbs if requested
    if (
      options.transformVerbs &&
      processedWord.endsWith('s') &&
      processedWord.length > 1
    ) {
      processedWord = transformVerb(processedWord);
    }

    // Capitalize first letter if not the first word
    if (index > 0) {
      processedWord =
        processedWord.charAt(0).toUpperCase() + processedWord.slice(1);
    }

    return processedWord;
  });

  // Join words and handle numbers if needed
  result = transformedWords.join('');

  // Handle numbers preservation in the final result
  if (!options.preserveNumbers) {
    // Remove numbers if not preserving them
    result = result.replace(/[0-9]/g, '');
  }

  // Ensure first character is lowercase
  result = result.charAt(0).toLowerCase() + result.slice(1);

  // Handle special operator mappings AFTER camelCase transformation
  const operatorMappings: Record<string, string> = {
    notContains: 'doesNotContain',
    notcontains: 'doesNotContain',
    lesthan: 'lessThan',
    lessthan: 'lessThan',
    lessthanorinclusive: 'lessThanInclusive',
    lessthanorequal: 'lessThanInclusive',
    lesthanorequal: 'lessThanInclusive',
    greaterthanorinclusive: 'greaterThanInclusive',
    greaterorequal: 'greaterThanInclusive',
    greaterthanorequal: 'greaterThanInclusive',
  };

  // Check if result matches an operator mapping (case-insensitive)
  const lowerResult = result.toLowerCase();
  if (operatorMappings[lowerResult]) {
    return operatorMappings[lowerResult];
  }

  return result;
};

export const getUtcDateTimeSpecialFormatFromISO = (): string => {
  const now = new Date();
  const isoString = now.toISOString(); // "2025-08-12T14:30:45.123Z"

  // Extract parts and format
  return isoString
    .replace(/[-T:]/g, '') // Remove separators
    .substring(0, 10); // Take first 10 characters (yyyyMMddHH)
};

const PHOTO_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'heic'];

// #66: the upload-url API expects a bare file extension (it becomes the S3 key
// suffix). The file name wins; the MIME subtype is the fallback. Anything
// outside the allow-list is null, so a MIME type is never passed through.
export const photoFileExtension = (
  file: Pick<File, 'name' | 'type'>
): string | null => {
  const name = file.name ?? '';
  const dot = name.lastIndexOf('.');
  const fromName = dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
  if (PHOTO_EXTENSIONS.includes(fromName)) return fromName;
  const type = (file.type ?? '').toLowerCase();
  const fromType = type.startsWith('image/') ? type.slice('image/'.length) : '';
  return PHOTO_EXTENSIONS.includes(fromType) ? fromType : null;
};

export const getPresignedUrl = async (
  fileExt: string,
  token: string,
  shopId: string
): Promise<PresignedUrlResponse> => {
  const response = await fetch(
    API_URL +
      '/s3/files/upload-url?file-extension=' +
      encodeURIComponent(fileExt),
    {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + token,
        'Shop-Id': shopId,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to get presigned URL');
  }

  return response.json();
};

export const uploadToS3 = async (
  file: File,
  presignedUrl: string
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        // Enhanced error with more details
        const errorDetails = {
          status: xhr.status,
          statusText: xhr.statusText,
          responseText: xhr.responseText,
          responseHeaders: xhr.getAllResponseHeaders(),
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          url: presignedUrl,
        };

        console.error('S3 Upload failed:', errorDetails);

        reject(
          new Error(
            `Upload failed with status ${xhr.status} (${xhr.statusText}). ` +
              `Response: ${xhr.responseText || 'No response body'}`
          )
        );
      }
    });

    xhr.addEventListener('error', event => {
      // Enhanced network error with more context
      const errorDetails = {
        event,
        readyState: xhr.readyState,
        status: xhr.status,
        statusText: xhr.statusText,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        url: presignedUrl,
      };

      console.error('S3 Upload network error:', errorDetails);

      reject(
        new Error(
          `Network error during upload. File: ${file.name} (${file.size} bytes). ` +
            `Ready state: ${xhr.readyState}, Status: ${xhr.status}`
        )
      );
    });

    // Optional: Add timeout handling
    xhr.addEventListener('timeout', () => {
      const errorDetails = {
        fileName: file.name,
        fileSize: file.size,
        timeout: xhr.timeout,
        url: presignedUrl,
      };

      console.error('S3 Upload timeout:', errorDetails);

      reject(
        new Error(
          `Upload timeout after ${xhr.timeout}ms for file: ${file.name}`
        )
      );
    });

    // Optional: Add progress tracking for debugging
    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) {
        const percentComplete = (event.loaded / event.total) * 100;
        console.debug(
          `Upload progress: ${percentComplete.toFixed(1)}% (${event.loaded}/${event.total} bytes)`
        );
      }
    });

    // Set timeout (optional - adjust as needed)
    xhr.timeout = 60000; // 60 seconds

    xhr.open('PUT', presignedUrl);

    // Set required headers
    xhr.setRequestHeader('Content-Type', file.type);

    // Don't set other headers that might trigger preflight unless necessary
    // LocalStack/S3 will handle most headers automatically

    xhr.send(file);
  });
};

export const THEMES = [
  { name: 'version1', label: 'Version 1', color: '#3b82f6' },
  { name: 'version2', label: 'Version 2', color: '#3b82f6' },
  { name: 'version3', label: 'Version 3', color: '#3b82f6' },
  { name: 'version4', label: 'Version 4', color: '#002213' },
  { name: 'version5', label: 'Version 5', color: '#0C0E12' },
];

// #64: the per-shop labels can only be requested once the shop is known (a
// fresh iframe has no shop id until POST /orders answers).
export const isKnownShopId = (id: number): boolean =>
  Number.isInteger(id) && id > 0;

type ShopLabels = Record<string, unknown>;

const fetchDynamicTranslations = async (
  language: string,
  shopId: number = SHOP_ID
): Promise<ShopLabels> => {
  const response = await fetch(
    `${API_URL}/translations/download?language=${language}`,
    {
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + JWT_TOKEN,
        'Shop-Id': shopId.toString(),
      },
    }
  );
  if (!response.ok) {
    throw new Error(`Dynamic translations for '${language}' failed.`);
  }
  return response.json();
};

// #95 language coordinator. Every language change (entry, switchers, the
// status page) goes through `setLanguage`; each call takes the next
// SELECTION generation and only the newest one may publish its shop labels
// or change the language, so a slower older load can never undo a newer
// selection. The language is applied once, at completion, so the visible
// labels stay readable while loading.
let translationsGeneration = 0;
// the newest selection while it is still loading; `epoch` is the shop epoch
// of the shop labels it loads (null: static only)
let pendingSelection: {
  generation: number;
  language: string;
  epoch: number | null;
} | null = null;
// the language the user asked for last: the pending one, else the applied one
const requestedLanguage = (): string =>
  pendingSelection?.language ?? i18n.language;

// #95 (D3): the SHOP epoch, distinct from the selection generation. It
// changes when the shop changes (`setShop` with another id) or its labels are
// replaced (#72 `dropDynamicTranslations`); labels downloaded for an older
// epoch are never published.
let shopEpoch = 0;

// Every label download takes the next revision; a response is published only
// while no newer download of the same language has been published, so an
// older answer (another selection, or a selection overtaken by the
// post-order refresh) never replaces a newer one.
type LabelDownload = {
  epoch: number;
  revision: number;
  labels: Promise<ShopLabels>;
};
let labelRevision = 0;
let publishedRevision: Record<string, number> = {};

const download = (language: string): LabelDownload => ({
  epoch: shopEpoch,
  revision: ++labelRevision,
  labels: fetchDynamicTranslations(language),
});

const publish = (
  language: string,
  from: LabelDownload,
  labels: ShopLabels,
  stillCurrent: () => boolean
): void => {
  if (
    !stillCurrent() ||
    from.epoch !== shopEpoch ||
    from.revision <= (publishedRevision[language] ?? 0)
  ) {
    return;
  }
  publishedRevision[language] = from.revision;
  i18n.addResourceBundle(language, 'dynamic', labels, true, true);
};

// The shop's English labels, downloaded once per shop epoch: `fallbackLng`
// takes from them every key the selected language lacks (404, {} or a
// partial answer). Only the download is shared; each caller publishes it
// through its own guard. A failed download is evicted, so the next
// selection retries it.
let shopEnglish: LabelDownload | null = null;

const dropShopLabels = () => {
  ++shopEpoch;
  shopEnglish = null;
  publishedRevision = {};
  Object.keys(i18n.store?.data ?? {}).forEach(code =>
    i18n.removeResourceBundle(code, 'dynamic')
  );
};

onShopChange = dropShopLabels;

// fresh: always download (the user asked for English itself); otherwise the
// download of this epoch is reused
const loadShopEnglish = (fresh: boolean): LabelDownload => {
  if (!fresh && shopEnglish !== null && shopEnglish.epoch === shopEpoch) {
    return shopEnglish;
  }
  const entry = download(DEFAULT_LANGUAGE);
  entry.labels.catch(() => {
    if (shopEnglish === entry) shopEnglish = null;
  });
  shopEnglish = entry;
  return entry;
};

const hasLabels = (labels: ShopLabels): boolean =>
  !!labels && typeof labels === 'object' && Object.keys(labels).length > 0;

// Downloads the shop labels of `language` and the shop English behind them,
// publishing each while `stillCurrent()` holds. A selection awaits both
// (`awaitEnglish`) before it commits the language; the post-order refresh
// awaits the shop English only when the requested answer is missing or
// empty (it then carries every label) - otherwise the English labels land
// on their own and the store binding re-renders.
const loadShopLabels = async (
  language: string,
  stillCurrent: () => boolean,
  awaitEnglish: boolean
): Promise<void> => {
  const english = language === DEFAULT_LANGUAGE;
  const requested = english ? loadShopEnglish(true) : download(language);
  const overlay = english ? null : loadShopEnglish(false);
  const overlayPublished = overlay?.labels.then(
    labels => publish(DEFAULT_LANGUAGE, overlay, labels, stillCurrent),
    error => console.warn(error, 'No shop English labels.')
  );
  let usable = false;
  try {
    const labels = await requested.labels;
    usable = hasLabels(labels);
    if (usable) publish(language, requested, labels, stillCurrent);
  } catch (error) {
    console.warn(error, 'Using the shop English labels.');
  }
  if (overlayPublished && (awaitEnglish || !usable)) {
    await overlayPublished;
  }
};

const ensureStaticTranslation = async (language: string): Promise<void> => {
  if (i18n.hasResourceBundle(language, 'translation')) return;
  const loader = lazyLocales[language];
  if (!loader) throw new Error(`Unsupported language '${language}'.`);
  const chunk = await loader();
  i18n.addResourceBundle(language, 'translation', chunk.default, true, true);
};

// The one language change: static translation (bundled, or its lazy chunk),
// then - with `withShopLabels` and a known shop - the shop labels of the
// language and the shop English, then ONE `changeLanguage`, if this is still
// the newest selection and - when it loaded shop labels - the shop it loaded
// them for is still the shop (a reset or another shop cancels it). A failed
// chunk keeps the current language (and whatever the user typed); the next
// selection retries it. Resolves true when the language was applied.
export async function setLanguage(
  language: string,
  {
    withShopLabels,
    withStartTexts = false,
  }: { withShopLabels: boolean; withStartTexts?: boolean }
): Promise<boolean> {
  const generation = ++translationsGeneration;
  const isCurrent = () => generation === translationsGeneration;
  // a selection started for a known shop is bound to it before the first
  // await: a reset or another shop meanwhile cancels it
  const boundEpoch =
    withShopLabels && isKnownShopId(SHOP_ID) ? shopEpoch : null;
  pendingSelection = { generation, language, epoch: boundEpoch };
  // review r2: before the entry selection is applied, the start texts follow
  // the requested language
  if (!startEntryApplied) notifyStart();
  // #493 (S3): the start-page texts of the URL shop for this language load
  // alongside (bounded, published under their own domain / language guard)
  const startGateDomain = startDomain;
  const startTexts =
    withStartTexts && startDomain !== null ? loadStartTexts(language) : null;
  try {
    try {
      await ensureStaticTranslation(language);
    } catch (error) {
      console.warn(error, `Keeping the language '${i18n.language}'.`);
      return false;
    }
    if (!isCurrent()) return false;
    if (boundEpoch !== null && boundEpoch !== shopEpoch) return false;
    if (withShopLabels && isKnownShopId(SHOP_ID)) {
      // a static-only start whose shop became known meanwhile binds now
      const epoch = boundEpoch ?? shopEpoch;
      pendingSelection = { generation, language, epoch };
      const stillCurrent = () => isCurrent() && epoch === shopEpoch;
      // #493 (S3): a hanging /download never holds the language; the shop
      // labels publish later, under the same guard
      await withinBudget(
        loadShopLabels(language, stillCurrent, true),
        LABELS_BUDGET_MS
      );
      if (!stillCurrent()) return false;
    }
    if (startTexts) await startTexts;
    if (!isCurrent()) return false;
    dropOtherStartTexts(language);
    await i18n.changeLanguage(language);
    return true;
  } finally {
    if (pendingSelection?.generation === generation) pendingSelection = null;
    // review r1: the start form opens only once the selection that loaded
    // its texts is over (the language applied, or kept on a failure), so it
    // never shows another language first; a newer selection opens it itself
    if (startTexts && isCurrent()) {
      if (!startEntryApplied && startGateDomain === startDomain) {
        startEntryApplied = true;
        notifyStart();
      }
      openStartGate(startGateDomain);
    }
  }
}

// #95 (E2): after POST /orders the shop is known: load its labels for the
// REQUESTED language (a pending selection included) without taking a new
// selection generation, so it never cancels a newer pending selection; it
// publishes only while no newer selection was made and the shop is
// unchanged, and its label revision keeps an older pending answer from
// replacing it. The language itself is applied by its own selection.
export async function refreshShopLabels(): Promise<void> {
  if (!isKnownShopId(SHOP_ID)) return;
  const generation = translationsGeneration;
  await loadShopLabels(
    requestedLanguage(),
    () => generation === translationsGeneration,
    false
  );
}

// #69: a language change that loads no shop labels (status page).
export const changeLanguageOnly = (language: string) =>
  setLanguage(language, { withShopLabels: false });

// The language change of the portal pages: with the shop labels.
export async function loadDynamicTranslations(language: string) {
  await setLanguage(language, { withShopLabels: true });
}

// #72: the full-screen QR entry replaces the 'dynamic' namespace when its
// verified shop is installed (or resets it): every bundle of a previous shop
// is dropped and the shop epoch changes, so a pending load can no longer
// publish into it, and a pending selection loading labels of the previous
// shop is cancelled (it never commits into the reset session).
export const dropDynamicTranslations = () => {
  if (pendingSelection && pendingSelection.epoch !== null) {
    pendingSelection = null;
  }
  dropShopLabels();
};

// #95 (E11): a shop label with the text to show when neither the selected
// language nor the shop English is available (both downloads failed): the
// entity's own name / instruction from the payload, or '' for an app message.
export const shopLabel = (key: string, fallback: string): string =>
  String(i18n.t(key, { defaultValue: fallback }));

// #69: labels of the verified shop of the status page live in their own
// namespace, so they never merge into (or survive in) the shared 'dynamic'
// bundle of the portal pages, and the remembered shop is never changed. Each
// load replaces the bundle of its language; a load for another shop first
// drops every bundle of the previous one. On failure the page shows the
// neutral status names.
export const STATUS_NS = 'status';
let statusShopId: number | null = null;
let statusGeneration = 0;

export async function loadStatusTranslations(
  shopId: number,
  language: string
): Promise<void> {
  const generation = ++statusGeneration;
  if (statusShopId !== shopId) {
    SUPPORTED_LANGUAGES.forEach(l =>
      i18n.removeResourceBundle(l.code, STATUS_NS)
    );
    statusShopId = shopId;
  }
  try {
    const translations = await fetchDynamicTranslations(language, shopId);
    if (generation !== statusGeneration) return;
    i18n.removeResourceBundle(language, STATUS_NS);
    i18n.addResourceBundle(language, STATUS_NS, translations);
  } catch (error) {
    if (generation !== statusGeneration) return;
    console.warn(error, 'Using the neutral status names.');
  }
}

// #493 (S3): the start page texts (11 labels) and the header caption
// (`start_header`) of the URL shop, from `GET /translations/start-page`
// (header `Shop: <domain>`, fail-open `{}`). They live in their own
// namespace, bound to the shop domain of the entry URL (never to the
// remembered shop id): `dropShopLabels` and the `/download` publications
// never touch it. Read in the selected language only (`startText`), never
// through `t('start:...')`, so `fallbackLng` cannot show the shop's English
// in another language; the bundles of the other languages are removed on a
// language change. Only the returns flow binds a domain: the standalone QR,
// the status page and `/withdrawal/*` never load them.
export const START_NS = 'start';
export const LABELS_BUDGET_MS = 1500;
export const START_TEXTS_CACHE_PREFIX = 'startTexts:';
export const START_LABELS = [
  'start_title',
  'start_order_number_label',
  'start_order_number_placeholder',
  'start_contact_label',
  'start_contact_placeholder',
  'start_help',
  'start_button',
  'start_how_title',
  'start_how_1',
  'start_how_2',
  'start_how_3',
  'start_header',
] as const;
export type StartLabel = (typeof START_LABELS)[number];
type StartTexts = Partial<Record<StartLabel, string>>;

// Resolves when `work` settles or after `ms`, whichever comes first.
const withinBudget = (work: Promise<unknown>, ms: number): Promise<void> =>
  new Promise(resolve => {
    const timer = setTimeout(resolve, ms);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    work.then(done, done);
  });

let startDomain: string | null = null;
// every publication (cache or answer) takes the next revision; an older one
// never replaces a newer one of the same language
let startRevision = 0;
let startPublished: Record<string, number> = {};
// review r1: the newest load per language (its revision); an answer of an
// older load of the same language (EN -> RO -> EN) never publishes nor
// caches. A language with an entry here is loaded or loading for the bound
// domain (`ensureStartTexts`).
let startNewestLoad: Record<string, number> = {};
// the render gate of the start form: opens once, on the first texts (cache
// or answer), a settled answer ({} / failure) or the budget
let startGateOpen = true;
// review r2: false from the binding of a domain until the first selection
// that loaded its texts is over; meanwhile the start texts are read in the
// REQUESTED language (the gate may be opened by the safety timer while the
// entry language is still being applied)
let startEntryApplied = true;
let startVersion = 0;
const startListeners = new Set<() => void>();
const notifyStart = () => {
  ++startVersion;
  startListeners.forEach(listener => listener());
};
export const subscribeStartTexts = (listener: () => void) => {
  startListeners.add(listener);
  return () => {
    startListeners.delete(listener);
  };
};
export const startTextsVersion = () => startVersion;
export const boundStartShop = () => startDomain;
export const isStartGateOpen = () => startGateOpen;
const openStartGate = (domain: string | null) => {
  if (domain !== startDomain || startGateOpen) return;
  startGateOpen = true;
  notifyStart();
};
export const releaseStartGate = () => openStartGate(startDomain);

const removeStartBundles = (keep: string | null) =>
  Object.keys(i18n.store?.data ?? {}).forEach(code => {
    if (code !== keep && i18n.hasResourceBundle(code, START_NS)) {
      i18n.removeResourceBundle(code, START_NS);
    }
  });

// The shop domain of the entry URL (null: this visit loads no start texts).
// Another domain drops every start bundle and closes the gate until its
// first texts settle.
export const bindStartShop = (domain: string | null) => {
  const normalized = domain?.trim().toLowerCase() || null;
  if (normalized === startDomain) return;
  startDomain = normalized;
  startPublished = {};
  startNewestLoad = {};
  startGateOpen = normalized === null;
  startEntryApplied = normalized === null;
  removeStartBundles(null);
  notifyStart();
};

const dropOtherStartTexts = (language: string) => {
  removeStartBundles(language);
  Object.keys(startNewestLoad).forEach(code => {
    if (code !== language) delete startNewestLoad[code];
  });
};

// the labels of the allow-list with a non-blank text (anything else is ignored)
const startTextsOf = (value: unknown): StartTexts | null => {
  if (
    value === null ||
    typeof value !== 'object' ||
    Object.getPrototypeOf(value) !== Object.prototype
  ) {
    return null;
  }
  const texts: StartTexts = {};
  START_LABELS.forEach(label => {
    const text = (value as Record<string, unknown>)[label];
    if (typeof text === 'string' && text.trim() !== '') texts[label] = text;
  });
  return texts;
};

const startCacheKey = (domain: string, language: string) =>
  `${START_TEXTS_CACHE_PREFIX}${domain}:${language}`;

const cachedStartTexts = (
  domain: string,
  language: string
): StartTexts | null => {
  try {
    const raw = localStorage.getItem(startCacheKey(domain, language));
    if (raw === null) return null;
    const texts = startTextsOf(JSON.parse(raw));
    return texts && Object.keys(texts).length > 0 ? texts : null;
  } catch {
    return null;
  }
};

const cacheStartTexts = (
  domain: string,
  language: string,
  texts: StartTexts
) => {
  try {
    const key = startCacheKey(domain, language);
    if (Object.keys(texts).length > 0) {
      localStorage.setItem(key, JSON.stringify(texts));
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // Storage may be unavailable; the answer still applies.
  }
};

// The texts of `language` for `domain`; `{}` when the shop changed nothing
// it can send (fail-open). Throws on a failure (status, network, shape).
const fetchStartTexts = async (
  domain: string,
  language: string
): Promise<StartTexts> => {
  const response = await fetch(
    `${API_URL}/translations/start-page?language=${encodeURIComponent(language)}`,
    { method: 'GET', headers: { Shop: domain } }
  );
  if (!response.ok) {
    throw new Error(`Start page texts for '${language}' failed.`);
  }
  const body = await response.json();
  const texts = startTextsOf(body?.texts);
  if (
    texts === null ||
    String(body?.language ?? '').toLowerCase() !== language.toLowerCase()
  ) {
    throw new Error(`Start page texts for '${language}' are malformed.`);
  }
  return texts;
};

// Published only while the domain is still the bound one, the language is
// still the requested one, the load is still the newest load of the language
// (review r1: the generation guard) and no newer publication of the language
// exists. Replaces the bundle of the language (a label the shop no longer
// sends goes back to its static text).
const publishStartTexts = (
  domain: string,
  language: string,
  load: number,
  revision: number,
  texts: StartTexts
): boolean => {
  if (
    domain !== startDomain ||
    language !== requestedLanguage() ||
    load !== startNewestLoad[language] ||
    revision <= (startPublished[language] ?? 0)
  ) {
    return false;
  }
  startPublished[language] = revision;
  if (i18n.hasResourceBundle(language, START_NS)) {
    i18n.removeResourceBundle(language, START_NS);
  }
  if (Object.keys(texts).length > 0) {
    i18n.addResourceBundle(language, START_NS, texts, true, true);
  }
  return true;
};

// A (domain, language) cache entry publishes at once and the selection does
// not wait; otherwise the answer is awaited up to the budget. The answer
// publishes under the guard, also late, and refreshes the cache only when it
// published (review r1: an older answer never overwrites a newer cache).
// The gate is opened by the selection once the language is applied.
const loadStartTexts = async (language: string): Promise<void> => {
  const domain = startDomain;
  if (domain === null) return;
  const load = ++startRevision;
  startNewestLoad[language] = load;
  const cached = cachedStartTexts(domain, language);
  if (cached)
    publishStartTexts(domain, language, load, ++startRevision, cached);
  const revision = ++startRevision;
  const answered = fetchStartTexts(domain, language).then(
    texts => {
      if (publishStartTexts(domain, language, load, revision, texts)) {
        cacheStartTexts(domain, language, texts);
      }
    },
    // silent, as GET /settings/portal: the static texts are the fallback
    () => undefined
  );
  if (!cached) await withinBudget(answered, LABELS_BUDGET_MS);
};

// review r1: on a route transition into the returns flow, loads the texts of
// the requested language when the bound domain has none loaded or loading
// for it (e.g. a language switched on the status page, then Back). The gate
// is already open: the texts apply when they arrive, under the guard.
export const ensureStartTexts = (): void => {
  if (startDomain === null) return;
  const language = requestedLanguage();
  if (startNewestLoad[language] !== undefined) return;
  void loadStartTexts(language);
};

// The shop text of `label` in the SELECTED language only, or undefined (the
// caller then shows its static text with its own `t`). Review r2: until the
// entry selection is applied, the selected language is the REQUESTED one
// (i18n is still on its start-up English): its shop text, else its static
// text (`staticKey`) when its static bundle is loaded (en and ro are
// bundled; the 20 others arrive as lazy chunks). Only while that chunk is
// still loading does the caller fall back to English - the accepted last
// resort; the chunk landing re-renders in the selected language.
export const startText = (
  label: StartLabel,
  staticKey?: string
): string | undefined => {
  const language = startEntryApplied ? i18n.language : requestedLanguage();
  const text = i18n.getResource(language, START_NS, label);
  if (typeof text === 'string' && text.trim() !== '') return text;
  if (
    staticKey &&
    language !== i18n.language &&
    i18n.hasResourceBundle(language, 'translation')
  ) {
    const fixed: unknown = i18n.getFixedT(
      language,
      'translation'
    )(staticKey as never);
    if (typeof fixed === 'string' && fixed !== '') return fixed;
  }
  return undefined;
};

export const getFeToken = async (): Promise<string> => {
  const response = await fetch(API_URL + '/fe-token', {
    method: 'POST',
    headers: {
      Authorization: 'Bearer ' + JWT_TOKEN,
      'Shop-Id': SHOP_ID.toString(),
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get fe token');
  }

  const data: ExternalToken = await response.json();
  return data.token;
};

// #51: only an http(s) URL activates the return conditions agreement; any
// other value (blank, non-string, javascript:, ftp:, bare host) is no URL.
export const termsUrlOrNull = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const url = value.trim();
  return /^https?:\/\//i.test(url) ? url : null;
};
